/**
 * Camada 1 · o `TransporteChat` que fala com o `services/messaging` por HTTP.
 *
 * MORA AQUI E NAO EM CADA APP, pela mesma razao que `criarFonteAjudaHttp` em
 * `@complexity/help`: escrito no app, ele nasceria uma vez por produto, e as copias
 * divergiriam na primeira mudanca de rota.
 *
 * O `assinar` NAO FALA COM O SERVIDOR, e isso esta dito e nao escondido: o fluxo ao vivo
 * exige uma decisao de transporte que ninguem tomou, e o `services/messaging` nao tem a
 * rota. Ele devolve um cancelador que nao faz nada, e a tela continua funcionando: o que
 * ela perde e a mensagem chegando sozinha, e nao a conversa inteira.
 */

import type {
  ComunicadoRecebido,
  Conversa,
  EstadoSino,
  Mensagem,
  TransporteChat,
} from './index.js';

/** Quem esta olhando. Decide o que conta como nao lida do outro lado. */
export type LadoDaConversa = 'cliente' | 'operacao';

/** A forma crua que o HTTP devolve: datas chegam como texto, e nao como `Date`. */
interface MensagemCrua {
  readonly id: string;
  readonly tenantId: string;
  readonly conversaId: string;
  readonly autor: Mensagem['autor'];
  readonly texto: string;
  readonly enviadaEm: string;
  readonly lidaEm: string | null;
  readonly sugestaoDeIA?: boolean;
}

/**
 * O comunicado como ele chega pela rede (ADR 0055).
 *
 * AS DATAS SAO `string` AQUI, e viram `Date` no mapeamento. E o mesmo par de
 * `MensagemCrua`/`Mensagem`, e existe pela mesma razao: JSON nao tem tipo de data, e sem o
 * par o resto do codigo receberia `string` acreditando ter `Date`.
 */
interface ComunicadoCru {
  readonly id: string;
  readonly assunto: string;
  readonly texto: string;
  readonly tipo: string;
  readonly publicadoEm: string;
  readonly lidoEm: string | null;
  readonly temRegistroDeLeitura: boolean;
}

interface ConversaCrua {
  readonly id: string;
  readonly tenantId: string;
  readonly assunto: string;
  readonly atualizadaEm: string;
  readonly naoLidas: number;
  readonly estado: 'aberta' | 'respondida' | 'encerrada';
  readonly encerradaEm: string | null;
}

export interface CredenciaisChat {
  /**
   * De onde sai o token e o tenant da chamada.
   *
   * FUNCAO E NAO VALOR: o token EXPIRA e e renovado, e um valor capturado na montagem
   * envelheceria dentro do componente sem ninguem perceber.
   */
  readonly obter: () => { readonly token?: string; readonly tenantId?: string };
}

export function criarTransporteChatHttp(
  base: string | undefined,
  lado: LadoDaConversa,
  credenciais?: CredenciaisChat,
): TransporteChat {
  const raiz = (base ?? '').replace(/\/+$/, '');

  /** Os cabecalhos, montados a cada chamada para o token nunca estar velho. */
  function cabecalhos(extra?: Record<string, string>): Record<string, string> {
    const c: Record<string, string> = { Accept: 'application/json', ...extra };
    const atual = credenciais?.obter();
    if (atual?.token) c.Authorization = `Bearer ${atual.token}`;
    // Omitido quando ausente, e nao mandado vazio: string vazia chega ao servidor como
    // tenant presente e falso, que e pior que tenant ausente.
    if (atual?.tenantId) c['X-Tenant-Id'] = atual.tenantId;
    return c;
  }

  async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
    if (raiz === '') {
      throw new Error('A base da API de mensagens não está configurada neste ambiente.');
    }
    const resposta = await fetch(`${raiz}${caminho}`, {
      // SEM `credentials`: a sessao viaja no cabecalho `Authorization`, e nao em cookie.
      // Com 'include', o navegador exige `Access-Control-Allow-Credentials` no preflight, o
      // servico nao o manda (decisao contra CSRF), e a chamada e recusada antes de sair.
      headers: cabecalhos({ 'Content-Type': 'application/json' }),
      ...init,
    });
    if (!resposta.ok) {
      throw new Error(`As mensagens responderam ${resposta.status}.`);
    }
    return (await resposta.json()) as T;
  }

  /**
   * Converte a data de texto para `Date`, uma vez, na fronteira.
   *
   * FEITO AQUI E NAO NA TELA de proposito: se cada componente convertesse, um deles
   * esqueceria, e `.toLocaleTimeString` num texto lanca em tempo de execucao. A fronteira e
   * o unico lugar onde a conversao acontece uma vez e vale para todos.
   */
  /** A data vira `Date` na FRONTEIRA, pelo mesmo motivo de `comData`. */
  function comDataDaConversa(c: ConversaCrua): Conversa {
    return {
      ...c,
      atualizadaEm: new Date(c.atualizadaEm),
      encerradaEm: c.encerradaEm ? new Date(c.encerradaEm) : null,
    };
  }

  function comData(m: MensagemCrua): Mensagem {
    return { ...m, enviadaEm: new Date(m.enviadaEm), lidaEm: m.lidaEm ? new Date(m.lidaEm) : null };
  }

  return {
    async listarConversas(_tenantId: string): Promise<readonly Conversa[]> {
      // SEM TOKEN, NAO SE CHAMA. Ver o cabecalho de `lerSino`: o 401 de uma chamada
      // prematura deixa erro na tela que a chamada boa nao apaga.
      if (!credenciais?.obter().token) return [];
      // O `tenantId` do contrato NAO vira parametro da URL: quem decide o cliente e a
      // sessao, no banco, e aceita-lo aqui daria a impressao de que o front escolhe. Ele
      // continua no contrato como segunda tranca, para poder ser conferido em qualquer log.
      const cruas = await pedir<readonly ConversaCrua[]>(`/conversas?como=${lado}`);
      return cruas.map(comDataDaConversa);
    },

    /**
     * Os comunicados que alcancam esta pessoa (ADR 0055).
     *
     * NUNCA LANCA, e devolve lista vazia na falha. A razao e a mesma do sino: o comunicado e
     * a fonte SECUNDARIA do painel, e derrubar a tela inteira porque ela nao respondeu
     * esconderia as conversas, que sao o assunto principal. Vazio aqui significa "nao ha
     * comunicado OU nao consegui olhar", e a tela nao promete distinguir os dois porque nao
     * ha o que a pessoa faria diferente em cada caso.
     */
    async listarComunicados(_tenantId: string): Promise<readonly ComunicadoRecebido[]> {
      if (!credenciais?.obter().token) return [];
      try {
        const cruas = await pedir<readonly ComunicadoCru[]>('/comunicados');
        return cruas.map((c) => ({
          ...c,
          // As datas chegam como texto e viram `Date` aqui, e nao na tela: uma tela que
          // recebe `string` as vezes e `Date` outras acaba com `new Date()` espalhado, e o
          // primeiro que esquecer produz "Invalid Date" no meio da lista.
          publicadoEm: new Date(c.publicadoEm),
          lidoEm: c.lidoEm ? new Date(c.lidoEm) : null,
        }));
      } catch {
        return [];
      }
    },

    /**
     * Marca um comunicado como lido.
     *
     * DEVOLVE `registrado`, e nao `void`: em quatro dos cinco alcances nao existe linha por
     * pessoa onde carimbar, e devolver nada faria a tela acreditar que gravou.
     */
    async marcarComunicadoLido(
      _tenantId: string,
      comunicadoId: string,
    ): Promise<{ registrado: boolean }> {
      if (!credenciais?.obter().token) return { registrado: false };
      try {
        const r = await pedir<{ registrado?: boolean }>(
          `/comunicados/${encodeURIComponent(comunicadoId)}/lido`,
          { method: 'POST' },
        );
        return { registrado: r.registrado === true };
      } catch {
        return { registrado: false };
      }
    },

    /** Ver o contrato: `historico=1` traz tambem as encerradas. */
    async listarComHistorico(_tenantId: string): Promise<readonly Conversa[]> {
      if (!credenciais?.obter().token) return [];
      const cruas = await pedir<readonly ConversaCrua[]>(
        `/conversas?como=${lado}&historico=1`,
      );
      return cruas.map(comDataDaConversa);
    },

    async abrir(
      _tenantId: string,
      produtoId: string,
      assunto: string,
      texto: string,
      tipo?: string,
    ): Promise<{ readonly id: string }> {
      return pedir<{ readonly id: string }>('/conversas', {
        method: 'POST',
        body: JSON.stringify({ produtoId, assunto, texto, tipo, como: lado }),
      });
    },

    async listarMensagens(_tenantId: string, conversaId: string): Promise<readonly Mensagem[]> {
      const id = encodeURIComponent(conversaId);
      const cruas = await pedir<readonly MensagemCrua[]>(`/conversas/${id}/mensagens`);
      return cruas.map(comData);
    },

    async enviar(_tenantId: string, conversaId: string, texto: string): Promise<Mensagem> {
      const id = encodeURIComponent(conversaId);
      const crua = await pedir<MensagemCrua>(`/conversas/${id}/mensagens`, {
        method: 'POST',
        body: JSON.stringify({ texto, como: lado }),
      });
      return comData(crua);
    },

    async marcarLida(_tenantId: string, mensagemId: string): Promise<void> {
      const id = encodeURIComponent(mensagemId);
      await pedir<{ ok: boolean }>(`/conversas/mensagens/${id}/lida`, {
        method: 'POST',
        body: JSON.stringify({ como: lado }),
      });
    },

    assinar(_tenantId: string, _aoReceber: (m: Mensagem) => void): () => void {
      // Ver o cabecalho: nao ha fluxo ao vivo ainda. Devolver um cancelador vazio mantem o
      // contrato honesto, porque quem chama precisa cancelar ao desmontar de qualquer jeito,
      // e no dia em que o fluxo existir nenhuma tela muda.
      return () => undefined;
    },
  };
}

/**
 * Le o numero do sino.
 *
 * FUNCAO SOLTA e nao metodo do transporte, porque o contrato `TransporteChat` nao a tem: o
 * sino aparece em toda tela, inclusive nas que nunca abrem conversa, e obriga-las a montar
 * o transporte inteiro para desenhar um numero seria acoplamento sem ganho.
 *
 * NUNCA LANCA. Falha vira `indisponivel`, que a tela ja sabe desenhar como marca propria em
 * vez de zero. Zero e "nenhuma nao lida"; indisponivel e "nao consegui olhar", e confundir
 * os dois faz a pessoa concluir que ninguem escreveu.
 */
export async function lerSino(
  base: string | undefined,
  lado: LadoDaConversa,
  credenciais?: CredenciaisChat,
): Promise<EstadoSino> {
  const raiz = (base ?? '').replace(/\/+$/, '');
  const cabecalhosSino = (): Record<string, string> => {
    const c: Record<string, string> = { Accept: 'application/json' };
    const atual = credenciais?.obter();
    if (atual?.token) c.Authorization = `Bearer ${atual.token}`;
    if (atual?.tenantId) c['X-Tenant-Id'] = atual.tenantId;
    return c;
  };
  // Sem base OU sem token, nao se chama: uma chamada sem `Authorization` toma 401 e deixa
  // o estado de erro na tela, e a chamada seguinte (ja com token) nao o desfaz. Da tela,
  // parece servico fora do ar quando o que faltava era a sessao ainda nao ter chegado.
  if (raiz === '' || !credenciais?.obter().token) {
    return { naoLidos: 0, medidoEm: null, indisponivel: true };
  }
  try {
    /*
      AS DUAS FONTES, EM PARALELO E NAO EM SEQUENCIA (ADR 0055).

      O sino desenha UM numero, e ele soma conversa e comunicado: para quem olha, a pergunta
      e uma so, *"tem algo esperando por mim?"*. Dois contadores lado a lado obrigariam a
      pessoa a somar de cabeca.

      `Promise.all` E NAO `await` seguido de `await`: em sequencia, o sino esperaria a soma
      dos dois tempos de rede em toda tela de todo produto, e ele e desenhado no cabecalho,
      que e a primeira coisa que aparece.

      O COMUNICADO E A FONTE OPCIONAL, e a assimetria e deliberada: se as conversas falham, o
      sino nao tem o que dizer e vira `indisponivel`. Se so os comunicados falham, o numero
      das conversas continua valendo, e some apenas a parcela que nao pode ser medida.
      Derrubar o sino inteiro por causa da fonte secundaria seria trocar informacao parcial
      por informacao nenhuma.
    */
    const [respostaConversas, respostaComunicados] = await Promise.all([
      fetch(`${raiz}/conversas/nao-lidas?como=${lado}`, { headers: cabecalhosSino() }),
      // NUNCA LANCA: `catch` devolvendo `null` mantem a promessa resolvida, e o `Promise.all`
      // so falharia inteiro se esta segunda rejeitasse.
      fetch(`${raiz}/comunicados/nao-lidos`, { headers: cabecalhosSino() }).catch(() => null),
    ]);

    if (!respostaConversas.ok) {
      return { naoLidos: 0, medidoEm: null, indisponivel: true };
    }
    const corpo = (await respostaConversas.json()) as { naoLidos: number; medidoEm: string };

    // A rota de comunicados e nova (ADR 0055) e pode nao existir no servico que esta no ar:
    // ausente, ela responde 404, e o sino continua contando as conversas em vez de sumir.
    let deComunicados = 0;
    if (respostaComunicados?.ok) {
      try {
        const c = (await respostaComunicados.json()) as { naoLidos?: number };
        deComunicados = typeof c.naoLidos === 'number' ? c.naoLidos : 0;
      } catch {
        // Corpo que nao e JSON e o caso de um proxy no meio devolvendo HTML. O numero das
        // conversas continua valendo, e este fica em zero.
        deComunicados = 0;
      }
    }

    return {
      naoLidos: corpo.naoLidos + deComunicados,
      naoLidosComunicados: deComunicados,
      medidoEm: new Date(corpo.medidoEm),
      indisponivel: false,
    };
  } catch {
    return { naoLidos: 0, medidoEm: null, indisponivel: true };
  }
}
